// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Route } from '../types';
import { esc, compact, pctv, num, countryColour, shortName, flag } from '../format';
import { niceMax } from '../charts';
import { navigate } from '../main';

export function renderTrends(root: HTMLElement, d: Dataset, _route: Route): void {
  const ag = d.classes.agricultural;
  const years = ag.years;
  // top 6 farmland countries by latest value, plus always China + UK
  const ranked = ag.countries.filter((c) => (c.value ?? 0) > 0).slice(0, 6);
  const series = ranked.map((c) => ({ name: c.name, iso: c.iso, colour: countryColour(c.iso, c.name), values: c.series }));

  root.innerHTML = `
    <div class="view-head">
      <h2>Trends over time</h2>
      <p>The farmland "country race" is the clearest trend in the whole Register: the popular belief that China is steadily buying up Australia's farmland is <strong>backwards</strong> — China peaked around ${compact(d.meta.counts.agChinaPeak)} ha and has sold down since, while the ${esc(shortName(d.meta.counts.agTop?.name ?? ''))} and Canada rose.</p>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Agricultural land held, by source country · ${years[0]}–${years[years.length - 1]}</h3>
      <p>Hectares of registered interest, controlling-country basis. Click a country in the legend to isolate its line.</p></div>
      <div class="chart-wrap" id="race"></div>
      <div class="legend" id="race-legend">${series.map((s, i) =>
        `<button class="legend-item" data-i="${i}" aria-pressed="true"><span class="cdot" style="background:${s.colour}"></span><span class="flagemoji">${flag(s.iso)}</span>${esc(shortName(s.name))}</button>`).join('')}</div>
    </div>

    <div class="two-col">
      <div class="panel">
        <div class="panel-head"><h3>Level of foreign ownership</h3><p>Share of the national total held by foreign interests. Broadly flat for a decade — not a steady climb.</p></div>
        <div class="chart-wrap" id="share"></div>
      </div>
      <div class="panel">
        <div class="panel-head"><h3>Residential acquisitions by foreign buyers</h3><p>A yearly flow of new purchases (mostly new dwellings). It has more than halved since 2017–18.</p></div>
        <div class="chart-wrap" id="res"></div>
      </div>
    </div>
  `;

  drawRace(root.querySelector('#race') as HTMLElement, years, series);
  drawShare(root.querySelector('#share') as HTMLElement, d);
  drawResidential(root.querySelector('#res') as HTMLElement, d);

  // legend isolate
  const legend = root.querySelector('#race-legend')!;
  const paths = () => Array.from((root.querySelector('#race') as HTMLElement).querySelectorAll<SVGGElement>('g.sline'));
  legend.querySelectorAll<HTMLButtonElement>('[data-i]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const anyOff = legend.querySelector('[aria-pressed="false"]');
      const i = +btn.dataset.i!;
      // if clicking the only-on one, reset all; else isolate this one
      const isolated = !anyOff;
      legend.querySelectorAll<HTMLButtonElement>('[data-i]').forEach((b, k) => {
        const on = isolated ? k === i : true;
        b.setAttribute('aria-pressed', String(on));
        paths()[k]?.classList.toggle('series-dim', !on);
      });
    });
  });
}

function axes(w: number, h: number, pad: { l: number; r: number; t: number; b: number }) {
  return { plotW: w - pad.l - pad.r, plotH: h - pad.t - pad.b };
}

function drawRace(el: HTMLElement, years: number[], series: { name: string; iso: string; colour: string; values: (number | null)[] }[]): void {
  const w = 900, h = 420, pad = { l: 54, r: 90, t: 14, b: 30 };
  const { plotW, plotH } = axes(w, h, pad);
  const max = niceMax(Math.max(...series.flatMap((s) => s.values.filter((v): v is number => v != null))));
  const x = (i: number) => pad.l + (i / (years.length - 1)) * plotW;
  const y = (v: number) => pad.t + plotH - (v / max) * plotH;
  const gridY = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);

  const grid = gridY.map((v) => `<line class="grid-line" x1="${pad.l}" y1="${y(v)}" x2="${pad.l + plotW}" y2="${y(v)}"/><text class="axis-text" x="${pad.l - 6}" y="${y(v) + 3}" text-anchor="end">${compact(v)}</text>`).join('');
  const xlabels = years.map((yr, i) => `<text class="axis-text" x="${x(i)}" y="${h - 10}" text-anchor="middle">${String(yr).slice(2)}</text>`).join('');

  const lines = series.map((s) => {
    const pts = s.values.map((v, i) => (v == null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`)).filter(Boolean).join(' ');
    const dots = s.values.map((v, i) => v == null ? '' :
      `<circle class="dot" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="3" fill="${s.colour}" stroke="#fff" stroke-width="1" data-tip="${esc(shortName(s.name))} · ${years[i]}\n${compact(v)} ha"/>`).join('');
    const last = [...s.values].map((v, i) => ({ v, i })).reverse().find((p) => p.v != null);
    const label = last ? `<text x="${x(last.i) + 6}" y="${y(last.v as number) + 3}" font-size="10" fill="${s.colour}" font-weight="600">${esc(shortName(s.name))}</text>` : '';
    return `<g class="sline"><polyline class="series-line" points="${pts}" stroke="${s.colour}"/>${dots}${label}</g>`;
  }).join('');

  el.innerHTML = `<svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="Agricultural land held by source country over time">
    ${grid}<line class="axis-line" x1="${pad.l}" y1="${pad.t + plotH}" x2="${pad.l + plotW}" y2="${pad.t + plotH}"/>
    ${xlabels}${lines}</svg>`;
}

function drawShare(el: HTMLElement, d: Dataset): void {
  const ag = (d.classes.agricultural.level ?? []).filter((r) => r.pct != null);
  const water = (d.classes.water.level ?? []).filter((r) => r.pct != null);
  const years = [...new Set([...ag, ...water].map((r) => r.year))].sort((a, b) => a - b);
  const w = 440, h = 260, pad = { l: 38, r: 14, t: 12, b: 26 };
  const { plotW, plotH } = axes(w, h, pad);
  const max = 20;
  const x = (yr: number) => pad.l + ((yr - years[0]) / (years[years.length - 1] - years[0])) * plotW;
  const y = (v: number) => pad.t + plotH - (v / max) * plotH;
  const grid = [0, 5, 10, 15, 20].map((v) => `<line class="grid-line" x1="${pad.l}" y1="${y(v)}" x2="${pad.l + plotW}" y2="${y(v)}"/><text class="axis-text" x="${pad.l - 5}" y="${y(v) + 3}" text-anchor="end">${v}%</text>`).join('');
  const xlabels = years.filter((_, i) => i % 2 === 0).map((yr) => `<text class="axis-text" x="${x(yr)}" y="${h - 8}" text-anchor="middle">${String(yr).slice(2)}</text>`).join('');
  const line = (rows: { year: number; pct: number | null }[], colour: string, label: string) => {
    const pts = rows.map((r) => `${x(r.year).toFixed(1)},${y(r.pct as number).toFixed(1)}`).join(' ');
    const dots = rows.map((r) => `<circle class="dot" cx="${x(r.year).toFixed(1)}" cy="${y(r.pct as number).toFixed(1)}" r="2.6" fill="${colour}" data-tip="${esc(label)} · ${r.year}\n${pctv(r.pct)}"/>`).join('');
    const last = rows[rows.length - 1];
    return `<g><polyline class="series-line" points="${pts}" stroke="${colour}"/>${dots}<text x="${x(last.year) - 4}" y="${y(last.pct as number) - 6}" font-size="10" fill="${colour}" text-anchor="end" font-weight="600">${esc(label)}</text></g>`;
  };
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="Level of foreign ownership over time">
    ${grid}<line class="axis-line" x1="${pad.l}" y1="${pad.t + plotH}" x2="${pad.l + plotW}" y2="${pad.t + plotH}"/>${xlabels}
    ${line(ag, 'var(--accent-primary)', 'Farmland')}${line(water, 'var(--accent-warm)', 'Water')}</svg>`;
}

function drawResidential(el: HTMLElement, d: Dataset): void {
  const flow = d.classes.residential.flow;
  if (!flow) { el.innerHTML = ''; return; }
  const years = flow.years;
  const types = Object.keys(flow.byType);
  const colours: Record<string, string> = { 'New property': 'var(--accent-primary)', 'Established property': 'var(--accent-warm)', 'Vacant land': '#8fc0ae' };
  const totalPerYear = (yr: number) => types.reduce((a, t) => a + Object.values(flow.byType[t]).reduce((s, byYear) => s + (byYear[yr] ?? 0), 0), 0);
  const stackForYear = (yr: number) => types.map((t) => ({ t, v: Object.values(flow.byType[t]).reduce((s, byYear) => s + (byYear[yr] ?? 0), 0) }));
  const max = niceMax(Math.max(...years.map(totalPerYear)));
  const w = 440, h = 260, pad = { l: 40, r: 14, t: 12, b: 26 };
  const { plotW, plotH } = axes(w, h, pad);
  const bw = (plotW / years.length) * 0.66;
  const x = (i: number) => pad.l + (i + 0.5) * (plotW / years.length);
  const y = (v: number) => pad.t + plotH - (v / max) * plotH;
  const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max).map((v) => `<line class="grid-line" x1="${pad.l}" y1="${y(v)}" x2="${pad.l + plotW}" y2="${y(v)}"/><text class="axis-text" x="${pad.l - 5}" y="${y(v) + 3}" text-anchor="end">${compact(v)}</text>`).join('');
  const bars = years.map((yr, i) => {
    let cy = pad.t + plotH;
    const segs = stackForYear(yr).map(({ t, v }) => {
      const hh = (v / max) * plotH;
      cy -= hh;
      return `<rect class="bar" x="${x(i) - bw / 2}" y="${cy.toFixed(1)}" width="${bw}" height="${hh.toFixed(1)}" fill="${colours[t] ?? '#ccc'}" data-tip="${esc(t)} · ${yr - 1}–${String(yr).slice(2)}\n${num(v)} acquisitions"/>`;
    }).join('');
    return `${segs}<text class="axis-text" x="${x(i)}" y="${h - 8}" text-anchor="middle">${String(yr).slice(2)}</text>`;
  }).join('');
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="Residential acquisitions by foreign buyers over time">
    ${grid}<line class="axis-line" x1="${pad.l}" y1="${pad.t + plotH}" x2="${pad.l + plotW}" y2="${pad.t + plotH}"/>${bars}</svg>
    <div class="legend">${types.map((t) => `<span class="legend-item"><span class="legend-swatch" style="background:${colours[t] ?? '#ccc'}"></span>${esc(t)}</span>`).join('')}</div>`;
  void navigate;
}
